import React, { Fragment, useCallback } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { ModalBody } from '@atlaskit/modal-dialog';
import { isReducedMotion } from '@atlaskit/motion';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { JiraModal as ModalDialog } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import { MountEvent, fireScreenAnalyticsDeferred } from '@atlassian/jira-product-analytics-bridge';
import { useMinimizableModal } from '../../../controllers/use-minimizable-modal/index.tsx';
import { TransitionState } from '../../../controllers/use-minimizable-modal/types.tsx';
import { useTransitionView } from '../../../controllers/use-transition-view/index.tsx';
import type { ModalContainerProps } from '../../../types.tsx';

/**
 * Note: we are forced to use this components type api instead of just using children
 * eg a 'children' api would look like the below
 * <MinimizableModal>
 *   <ModalBody>
 *     {children}
 *   </ModalBody>
 * </MinimizableModal>
 *
 * This is because ModalBody needs to be within ModalDialog to get its context.
 * Since the ModalDialog parent is not a true parent due to the reparenter,
 * this causes ModalBody to not have access to the context and then throwing an error.
 */

export const Modal = ({
	children,
	components,
	onClose: onCloseFromProps,
	shouldCloseOnOverlayClick,
	'data-testid': dataTestId,
	...rest
}: ModalContainerProps) => {
	const { onClose, isTransitionInState } = useMinimizableModal();

	const { onFinishAnimation } = useTransitionView('modal', true);

	const onOpenComplete = () => {
		onFinishAnimation('entering');
	};
	const onCloseComplete = () => {
		onFinishAnimation('exiting');
	};
	const isViewEntered = isTransitionInState(['modal'], TransitionState.ENTERED);

	const { Header = Fragment, Body = ModalBody, Footer = Fragment } = components || {};

	const onMount = useCallback((event: UIAnalyticsEvent) => {
		fireScreenAnalyticsDeferred(event, {
			isReducedMotion: isReducedMotion(),
		});
	}, []);

	return (
		<ShortcutScope>
			<ModalDialog
				width="large"
				height={undefined}
				autoFocus={false}
				onClose={onCloseFromProps || onClose}
				shouldCloseOnEscapePress
				shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
				testId={
					isViewEntered ? dataTestId || 'minimizable-modal.ui.modal-container.modal' : undefined
				}
				messageId="minimizable-modal.ui.modal-container.modal.modal-dialog"
				messageType="transactional"
				onOpenComplete={onOpenComplete}
				onCloseComplete={onCloseComplete}
				{...rest}
			>
				<MountEvent onMount={onMount} />
				<Header />
				<Body>{children === undefined ? null : children}</Body>
				<Footer />
			</ModalDialog>
		</ShortcutScope>
	);
};
